"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronRight, FileText, Calendar } from "lucide-react";
import { ICMS } from "@/types/home.types";
import { createLookupObject } from "@/utils/helper";

interface CancellationPolicyContentProps {
  cmsData?: ICMS[];
}

export function CancellationPolicyContent({
  cmsData,
}: CancellationPolicyContentProps) {
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // Premium, professionally designed default cancellation policy fallback
  const defaultPolicyTemplate = `
    <div>
      <h2 class="text-2xl font-bold font-sans text-gray-955 mb-4">1. Cancellation Timelines & Refunds</h2>
      <p class="text-base text-gray-600 leading-relaxed font-sans font-normal mb-4">Refund calculations are based on when the cancellation request is received relative to the scheduled check-in date:</p>
      <ul class="space-y-3.5 pl-6 text-gray-600 list-disc font-sans font-normal mb-6">
        <li><span class="font-semibold text-gray-900">30 days or more prior:</span> 100% refund of the total booking amount (subject to a nominal 10% administration/processing fee).</li>
        <li><span class="font-semibold text-gray-900">15 to 29 days prior:</span> 50% refund of the total booking amount.</li>
        <li><span class="font-semibold text-gray-900">7 to 14 days prior:</span> 25% refund of the total booking amount.</li>
        <li><span class="font-semibold text-gray-900">Less than 7 days prior:</span> Non-refundable (100% cancellation charges apply).</li>
      </ul>
    </div>
    <div>
      <h2 class="text-2xl font-bold font-sans text-gray-955 mb-4">2. Booking Amendments & Date Changes</h2>
      <p class="text-base text-gray-600 leading-relaxed font-sans font-normal mb-4">We understand plans can change. Amendments to booking dates or tent categories are governed by the following rules:</p>
      <ul class="space-y-3.5 pl-6 text-gray-600 list-disc font-sans font-normal mb-6">
        <li>Amendments are subject to tent availability and seasonal rate hikes at the time of modification.</li>
        <li>A nominal administration fee of ₹3,000/- per tent plus applicable taxes is charged for date changes.</li>
        <li>No amendments are permitted within 7 days of the scheduled check-in date.</li>
      </ul>
    </div>
    <div>
      <h2 class="text-2xl font-bold font-sans text-gray-955 mb-4">3. No-Show & Early Departures</h2>
      <p class="text-base text-gray-600 leading-relaxed font-sans font-normal mb-4">In case of a no-show or early checkout, the following terms apply:</p>
      <ul class="space-y-3.5 pl-6 text-gray-600 list-disc font-sans font-normal mb-6">
        <li>If a guest fails to check in on the scheduled date (No-Show), the booking will be treated as cancelled, and no refund will be issued.</li>
        <li>No refund or credit will be provided for early departures or unused portions of the booked packages.</li>
      </ul>
    </div>
    <div>
      <h2 class="text-2xl font-bold font-sans text-gray-955 mb-4">4. Force Majeure & Special Situations</h2>
      <p class="text-base text-gray-600 leading-relaxed font-sans font-normal mb-4">Refund policies during unforeseen circumstances:</p>
      <p class="text-base text-gray-600 leading-relaxed font-sans font-normal mb-4">Rann Utsav is a seasonal event in the desert. We are not liable to pay any refunds or compensation for cancellations arising out of force majeure events, including but not limited to: natural disasters, sudden extreme weather anomalies, government border permit rules, national strikes, or pandemics.</p>
    </div>
    <div>
      <h2 class="text-2xl font-bold font-sans text-gray-955 mb-4">5. Refund Processing Time</h2>
      <p class="text-base text-gray-600 leading-relaxed font-sans font-normal">Approved refunds will be processed back to the original payment source within 7 to 10 working days from the date of formal cancellation approval.</p>
    </div>
  `;

  const cancellationPolicyHtml = lookUpCMSData?.["Cancellation Policy"]?.Template || defaultPolicyTemplate;

  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
      {/* Dynamic Top Decorative Gradient Bar */}
      <div className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 h-1.5 w-full"></div>

      <div className="flex-grow py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-8 font-sans">
            <Link href="/" className="hover:text-pink-600 transition">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-900 font-medium">Cancellation Policy</span>
          </nav>

          {/* Title Header Section */}
          <div className="mb-10 text-center flex flex-col items-center justify-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-pink-50 rounded-2xl mb-4 border border-pink-100">
              <FileText className="w-6 h-6 text-pink-600" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 font-sans mb-3 leading-tight">
              Cancellation Policy
            </h1>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 font-medium">
              <Calendar className="w-4.5 h-4.5 text-pink-500" />
              <span>Last updated: May 2026</span>
            </div>
          </div>

          {/* Main Card Content */}
          <div
            className="bg-white rounded-3xl border border-gray-150 p-8 sm:p-12 shadow-sm space-y-10"
            dangerouslySetInnerHTML={{
              __html: cancellationPolicyHtml,
            }}
          />
        </div>
      </div>
    </div>
  );
}
